import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReviewPointDetail } from '@/core/models/approval.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { resolveDefaultPeriodId } from '@/core/periodDefault';
import { approvalService } from '@/core/services/approval';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { AccountingPeriodSelect, PERIODS_ROUTE, currentPeriod, displayDate, useClientGuard } from './shared';
import './TaxreturnReviewpoints.scss';

const STATUSES = ['Open', 'Responded', 'Cleared'];

/** Review points workflow for a period: raise a point, respond, clear, and re-confirm a stale cleared point. */
export function TaxreturnReviewpoints() {
  const navigate = useNavigate();
  const client = useClientGuard();

  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [points, setPoints] = useState<ReviewPointDetail[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  /** New review point being raised. */
  const [newObjectType, setNewObjectType] = useState('Period');
  const [newObjectId, setNewObjectId] = useState('');
  const [newText, setNewText] = useState('');

  /** Per-point response drafts, keyed by review point id. */
  const [responseDrafts, setResponseDrafts] = useState<Record<string, string>>({});

  const load = (periodId: string | null, status: string | null) => {
    if (!periodId) {
      return;
    }
    approvalService.getReviewPoints(periodId, status).then(
      (result) => setPoints(result ?? []),
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    practiceService.getClientPeriods(client.id).then(
      async (response) => {
        const list = response.periods ?? [];
        setPeriods(list);
        const id = (await resolveDefaultPeriodId(client.id, list)) ?? currentPeriod(list)?.id ?? null;
        setSelectedPeriodId(id);
        if (id) {
          load(id, null);
        } else {
          notifyError('Add an accounting period for this client first.');
        }
      },
      (error) => notifyError(error),
    );
  }, [client]);

  const onPeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    load(periodId, statusFilter);
  };

  /** A raise, respond or clear request is running (stops a second click). */
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const begin = () => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };
  const finish = () => {
    savingRef.current = false;
    setSaving(false);
  };

  const raise = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!newText.trim()) {
      notifyError('Enter the review point.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .raiseReviewPoint(periodId, {
        objectType: newObjectType || 'Period',
        objectId: newObjectId || periodId,
        text: newText.trim(),
        assignedTo: null,
      })
      .then(
        (result) => {
          setPoints(result ?? []);
          setNewText('');
          setNewObjectId('');
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const respond = (point: ReviewPointDetail) => {
    const periodId = selectedPeriodId;
    const text = (responseDrafts[point.reviewPointId] ?? '').trim();
    if (!periodId || !text) {
      notifyError('Enter a response.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .respondReviewPoint(periodId, point.reviewPointId, text)
      .then(
        (result) => {
          setPoints(result ?? []);
          setResponseDrafts((drafts) => ({ ...drafts, [point.reviewPointId]: '' }));
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const clear = (point: ReviewPointDetail) => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .clearReviewPoint(periodId, point.reviewPointId)
      .then(
        (result) => {
          setPoints(result ?? []);
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const staleCount = points.filter((p) => p.isStale).length;
  const openCount = points.filter((p) => p.status !== 'Cleared').length;

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-reviewpoints">
      <div className="sidenav-details">
        <div className="sidenav-header">
          <div className="d-flex align-items-center justify-space-between">
            <h2>Review Points</h2>
            <Button className="close-btn" aria-label="Close" onClick={() => void navigate(PERIODS_ROUTE)}>
              <Icon name="close" />
            </Button>
          </div>
        </div>

        <div className="sidenav-body">
          <div className="form-field-title pt-1 pb-1 d-flex align-items-center justify-space-between">
            <div className="d-flex align-items-center">
              <span className="pr-1">Accounting period</span>
              <AccountingPeriodSelect periods={periods} value={selectedPeriodId} onChange={onPeriodChange} />
              <span className="pl-2 pr-1">Status</span>
              <Select<string>
                className="w-150"
                items={STATUSES}
                placeholder="All"
                value={statusFilter}
                onChange={(value) => {
                  const status = (value as string | null) ?? null;
                  setStatusFilter(status);
                  load(selectedPeriodId, status);
                }}
              />
            </div>
            <div>
              <span className="status-chip">{openCount} outstanding</span>
              {staleCount > 0 && <span className="status-chip stale-chip ml-1">{staleCount} stale</span>}
            </div>
          </div>

          {/* Raise a new point */}
          <div className="raise-box">
            <div className="raise-grid">
              <InputField
                fieldClassName="raise-field"
                placeholder="Object type"
                charMode="text"
                value={newObjectType}
                onChange={(event) => setNewObjectType(event.target.value)}
              />
              <InputField
                fieldClassName="raise-field"
                placeholder="Object id (optional)"
                charMode="alphanumeric"
                value={newObjectId}
                onChange={(event) => setNewObjectId(event.target.value)}
              />
              <InputField
                fieldClassName="raise-field raise-text"
                placeholder="Review point"
                maxLength={1000}
                value={newText}
                onChange={(event) => setNewText(event.target.value)}
              />
              <Button className="action-button primary-button border-radius-6" busy={saving} onClick={raise}>
                <Icon name="add" />
                <span>Raise</span>
              </Button>
            </div>
          </div>

          {/* The points themselves */}
          <div className="review-list">
            {points.map((point) => (
              <div key={point.reviewPointId} className={`review-card${point.isStale ? ' stale' : ''}`}>
                <div className="d-flex align-items-center justify-space-between">
                  <div>
                    <span className="status-chip">{point.status}</span>
                    {point.isStale && (
                      <span
                        className="status-chip stale-chip ml-1"
                        title="The trial balance or journals have changed since this point was raised or cleared."
                      >
                        Stale
                      </span>
                    )}
                    <span className="object-ref ml-1">
                      {point.objectType} &middot; {point.objectId}
                    </span>
                  </div>
                  <div className="text-muted">{displayDate(point.raisedAtUtc)}</div>
                </div>

                <div className="review-text">{point.text}</div>

                {/* #141: say what "stale" means and how it goes away. */}
                {point.isStale && (
                  <div className="alert-inline d-flex align-items-center justify-space-between gap-15">
                    <span>
                      The figures (trial balance or journals) have changed since this point was{' '}
                      {point.status === 'Cleared' ? 'cleared' : 'raised'}, so it is marked stale. Check it against the
                      current figures, then{' '}
                      {point.status === 'Cleared' ? 'confirm it is still cleared' : 'respond or clear it'}; that records
                      it against the current figures and removes the stale mark.
                    </span>
                    {point.status === 'Cleared' && (
                      <Button className="action-button secondary-button border-radius-6" busy={saving} onClick={() => clear(point)}>
                        <Icon name="done_all" />
                        <span>Confirm still cleared</span>
                      </Button>
                    )}
                  </div>
                )}

                {/* The conversation */}
                {point.responses.map((response) => (
                  <div className="response" key={response.reviewPointResponseId}>
                    <div className="response-meta">
                      {response.respondedByName || 'User'} &middot; {displayDate(response.respondedAtUtc)}
                    </div>
                    <div>{response.responseText}</div>
                  </div>
                ))}

                {point.status !== 'Cleared' && (
                  <div className="respond-row">
                    <InputField
                      fieldClassName="respond-input"
                      placeholder="Respond to this point"
                      maxLength={1000}
                      value={responseDrafts[point.reviewPointId] ?? ''}
                      onChange={(event) =>
                        setResponseDrafts((drafts) => ({ ...drafts, [point.reviewPointId]: event.target.value }))
                      }
                    />
                    <Button className="action-button secondary-button border-radius-6" busy={saving} onClick={() => respond(point)}>
                      <span>Respond</span>
                    </Button>
                    <Button className="action-button primary-button border-radius-6" busy={saving} onClick={() => clear(point)}>
                      <Icon name="done" />
                      <span>Clear</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {!points.length && <div className="text-muted pt-1">No review points for this period.</div>}
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}
